import { dsDisabled, dsSunsetRegistryAppId } from "@/config";
import { dSEnvironmentConfig, dSSunsetEnvironmentConfig } from "@/lib/clients";
import { queryClient } from "@/lib/query-client";
import { DSContractListingExtd, DSContractListingMap } from "@/lib/types";
import { findListingByLstName } from "@/lib/utils";
import { DualStake } from "@myth-finance/dualstake-ts-sdk";
import { useQuery } from "@tanstack/react-query";

export const useListings = (account?: string, sunset?: boolean) => {
  return useQuery({
    queryKey: ["listings", account ?? "anon", sunset ? "s" : "n"],
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<DSContractListingMap> => {
      if (dsDisabled) return new Map();
      if (sunset && !dsSunsetRegistryAppId) {
        console.warn(
          "Sunset listings requested but no sunset registry found in this environment"
        );
        return new Map();
      }
      console.debug("Requesting", sunset ? "sunset" : "", "Listings");
      const listings: DSContractListingMap = await DualStake.getAvailableContractListings({
        ...(sunset ? dSSunsetEnvironmentConfig : dSEnvironmentConfig),
        sender: account ?? dSEnvironmentConfig.sender,
      })
        .catch((_) =>
          DualStake.getAvailableContractListings(dSEnvironmentConfig)
        )
        .catch((e: Error) => {
          throw new Error(`While loading listings: ${e?.message ?? e}`);
        });
      if (sunset) {
        for(const listing of listings.values()) {
          listing.sunset = true;
        }
      }
      return listings
    },
  });
};

export const useListing = (
  lstName: string,
  account?: string,
  sunset?: boolean
) => {
  const globalCacheKey = ["listings", account ?? "anon", sunset ? "s" : "n"];
  return useQuery({
    queryKey: ["listing", lstName, account ?? "anon", sunset ? "s" : "n"],
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<DSContractListingExtd | null> => {
      if (dsDisabled || !lstName) return null;

      // look in global listings cache
      const cached = queryClient.getQueryData(
        globalCacheKey
      ) as DSContractListingMap | null;
      if (cached) {
        return findListingByLstName(cached, lstName) ?? null;
      }
      if (sunset && !dsSunsetRegistryAppId) {
        console.warn(
          "Sunset listing",
          lstName,
          "requested but no sunset registry found in this environment"
        );
        return null;
      }
      console.debug("Requesting", sunset ? "sunset" : "", "Listing", lstName);
      const listings = await DualStake.getAvailableContractListings({
        ...(sunset ? dSSunsetEnvironmentConfig : dSEnvironmentConfig),
        sender: account ?? dSEnvironmentConfig.sender,
      })
        .catch((_) =>
          DualStake.getAvailableContractListings(
            sunset ? dSSunsetEnvironmentConfig : dSEnvironmentConfig
          )
        )
        .catch((e: Error) => {
          throw new Error(`While loading listings: ${e?.message ?? e}`);
        });
      const listing = findListingByLstName(listings, lstName) ?? null;
      if (listing && sunset) {
        return {
          ...listing,
          sunset: true,
        };
      }
      return listing;
    },
  });
};

export const clearListingsCache = () => {
  queryClient.invalidateQueries({ queryKey: ["listing"] });
  queryClient.invalidateQueries({ queryKey: ["listings"] });
};
